import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import logo from '@/assets/logo.png';
import FormField from '@/components/common/FormField';
import Button from '@/components/common/Button';
import { errorTypes } from '@/utility/validation';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { showToastNotification } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { 
  IForgotPasswordForm, IVerifyOtpForm, 
  IForgotPasswordResponse, IVerifyOtpResponse 
} from '@/interfaces/Auth/forgotPassword.interface';

export default function ForgotPassword() {
  const [step, setStep] = useState<1 | 2>(1);
  const [identifier, setIdentifier] = useState<string>('');
  const [otpValues, setOtpValues] = useState<string[]>(Array(4).fill(''));
  const navigate = useNavigate();

  // Form for Step 1
  const {
    register: registerEmail,
    handleSubmit: handleSubmitEmail,
    formState: { errors: errorsEmail, isSubmitting: isSubmittingEmail }
  } = useForm<IForgotPasswordForm>();

  // Form for Step 2
  const {
    register: registerOtp,
    handleSubmit: handleSubmitOtp,
    setValue: setOtpValue,
    formState: { errors: errorsOtp, isSubmitting: isSubmittingOtp }
  } = useForm<IVerifyOtpForm>();

  const handleOtpChange = (index: number, value: string) => {
    if (!/^[0-9]*$/.test(value)) return;
    
    const newOtp = [...otpValues];
    newOtp[index] = value.substring(value.length - 1);
    setOtpValues(newOtp);
    setOtpValue('otp', newOtp.join(''), { shouldValidate: true });

    if (value && index < 3) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    const pasteData = e.clipboardData.getData('text').slice(0, 4).replace(/[^0-9]/g, '');
    if (pasteData) {
      const newOtp = [...otpValues];
      for (let i = 0; i < pasteData.length; i++) {
        newOtp[i] = pasteData[i];
      }
      setOtpValues(newOtp);
      setOtpValue('otp', newOtp.join(''), { shouldValidate: true });
      const nextIndex = pasteData.length < 4 ? pasteData.length : 3;
      document.getElementById(`otp-${nextIndex}`)?.focus();
    }
  };

  const onSubmitEmail = async (data: IForgotPasswordForm) => {
    try {
      const trimmedIdentifier = data.identifier.trim();
      const response = await callApi<IForgotPasswordResponse>(`${API_URL}${API_ENDPOINT.forgotPassword}`, {
        method: 'POST',
        data: { identifier: trimmedIdentifier },
      });
      setIdentifier(trimmedIdentifier);
      setStep(2);
      showToastNotification('success', response?.message || 'OTP sent successfully');
    } catch (error) {
      ThrowError(error);
    }
  };

  const onSubmitOtp = async (data: IVerifyOtpForm) => {
    try {
      const response = await callApi<IVerifyOtpResponse>(`${API_URL}${API_ENDPOINT.forgotPassword}`, {
        method: 'POST',
        data: {
          identifier: identifier,
          otp: data.otp.trim()
        },
      });
      // The API returns a verification token for resetting
      showToastNotification('success', 'OTP verified successfully');
      navigate(`${RoutesLink.resetPassword}/${response?.data?.verificationToken}`);
    } catch (error) {
      ThrowError(error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, hsl(var(--primary)) 2px, transparent 2px), radial-gradient(circle at 75% 75%, hsl(var(--primary)) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
      </div>

      <div className="w-full max-w-md relative z-10">
        <div className="bg-card rounded-xl border shadow-2xl p-8">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img src={logo} alt="Yebo Eggs" className="w-24 h-24 mb-3 rounded-full shadow-lg" />
            <h1 className="text-xl font-bold text-foreground">
              {step === 1 && "Forgot Password"}
              {step === 2 && "Verify OTP"}
            </h1>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              {step === 1 && "Enter your email or identifier to receive an OTP."}
              {step === 2 && `Enter the OTP sent to ${identifier}`}
            </p>
          </div>

          {step === 1 && (
            <form className="space-y-4" onSubmit={handleSubmitEmail(onSubmitEmail)}>
              <FormField
                name="Identifier"
                type="email"
                className="w-full h-10 px-3 rounded-md border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="admin@yeboeggs.co.za"
                {...registerEmail('identifier', {
                  ...errorTypes.required('Email'),
                  ...errorTypes.email,
                })}
                requiredIcon={true}
                error={errorsEmail.identifier?.message}
              />
              <Button type="submit" fullWidth isLoading={isSubmittingEmail} loadingText="Sending OTP...">
                Send OTP
              </Button>
            </form>
          )}

          {step === 2 && (
            <form className="space-y-4" onSubmit={handleSubmitOtp(onSubmitOtp)}>
              <div>
                <label className="block text-xs font-medium text-foreground mb-3 text-center">
                  Enter 4-digit OTP
                </label>
                <div className="flex justify-center gap-4" onPaste={handleOtpPaste}>
                  {otpValues.map((digit, idx) => (
                    <FormField
                      key={idx}
                      name={`otp-${idx}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      wrapperClass=""
                      inputClass="w-12 h-14 text-center text-xl font-bold rounded-xl border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all shadow-sm"
                    />
                  ))}
                </div>
                {/* Hidden input for react-hook-form validation */}
                <input
                  type="hidden"
                  {...registerOtp('otp', {
                    required: 'OTP is required',
                    minLength: { value: 4, message: "OTP must be 4 digits" },
                    maxLength: { value: 4, message: "OTP must be 4 digits" }
                  })}
                />
                {errorsOtp.otp?.message && (
                  <p className="text-[10px] font-medium text-destructive mt-2 text-center">{errorsOtp.otp.message}</p>
                )}
              </div>
              <Button type="submit" fullWidth isLoading={isSubmittingOtp} loadingText="Verifying...">
                Verify OTP
              </Button>
              <div className="flex justify-center mt-2">
                <button type="button" onClick={() => setStep(1)} className="text-xs text-muted-foreground hover:text-primary transition-colors">
                  Wrong email? Go back
                </button>
              </div>
            </form>
          )}

          {step === 1 && (
            <div className="mt-6 text-center">
              <Link to={RoutesLink.login} className="text-xs font-medium text-muted-foreground hover:text-primary transition-colors">
                Back to Login
              </Link>
            </div>
          )}
        </div>
        <p className="text-center text-[10px] text-muted-foreground mt-4">© {new Date().getFullYear()} Yebo Eggs (Pty) Ltd · South Africa</p>
      </div>
    </div>
  );
}
